// ==================================================
// IMPORTS
// ==================================================
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import './Notepad.css';
import '../../components/shared/StatusBanner.css';
import api from "../../utils/api";
import { CheckSquare, X, Trash2, ArrowUpCircle, FileText, FileDown } from "lucide-react";
import KuddleSelect from "../KuddleSelect";

// ==================================================
// API HELPERS
// Purpose:
// Centralized API calls used by Notepad.
// ==================================================
async function apiGetNotes({ tag, search, centerId } = {}) {
  const params = new URLSearchParams();
  if (tag) params.set("tag", tag);
  if (search) params.set("search", search);
  if (centerId) params.set("centerId", centerId);

  const res = await api.get(`/api/notes?${params.toString()}`);
  return res.data;
}

async function apiCreateNote(payload) {
  const res = await api.post("/api/notes", payload);
  return res.data;
}

async function apiUpdateNote(id, payload) {
  const res = await api.put(`/api/notes/${id}`, payload);
  return res.data;
}

async function apiCreateNotification(payload) {
  const res = await api.post("/api/notifications", payload);
  return res.data;
}

async function apiDeleteNote(id) {
  const res = await api.delete(`/api/notes/${id}`);
  return res.data;
}

async function apiBulkDelete(ids) {
  const res = await api.post("/api/notes/bulk-delete", { ids });
  return res.data;
}

async function apiCreateReminder(payload) {
  const res = await api.post("/api/reminders", payload);
  return res.data;
}

async function apiLogExport(payload) {
  try {
    const res = await api.post("/api/export-logs", payload);
    return res.data;
  } catch {
    return null;
  }
}

// ==================================================
// DATA FORMATTING UTILITIES
//
// Purpose:
// Formats note data for storage, display, and export.
//
// Examples:
// Tag formatting
// CSV row formatting
//
// ==================================================
function normalizeTags(tagsInput) {
  const raw = String(tagsInput || "").trim();
  if (!raw) return [];

  const parts = raw
    .replace(/,/g, " ")
    .split(/\s+/)
    .map(t => t.trim())
    .filter(Boolean)
    .map(t => (t.startsWith("#") ? t : `#${t}`));

  return Array.from(new Set(parts));
}

function toCsvRow(values) {
  // Basic CSV escaping: wrap in quotes and escape inner quotes
  return values
    .map(v => `"${String(v ?? "").replace(/"/g, '""')}"`)
    .join(",");
}

function notesToCsv(notes) {
  const header = toCsvRow([
    "Note",
    "Tags",
    "Date Created",
    "Event Date",
    "Last Updated",
  ]);

  const rows = notes.map(n =>
    toCsvRow([
      n.content,
      (n.tags || []).join(" "),
      n.createdAt ? new Date(n.createdAt).toLocaleString() : "",
      n.scheduledAt ? new Date(n.scheduledAt).toLocaleString() : "",
      n.updatedAt ? new Date(n.updatedAt).toLocaleString() : "",
    ])
  );

  return [header, ...rows].join("\n");
}
// ==================================================
// FILE EXPORT UTILITIES
//
// Purpose:
// Handles downloading and exporting notes.
//
// Examples:
// CSV Export
// PDF Export
// File Downloads
//
// ==================================================

function downloadBlob(filename, contentType, data) {
  const blob = new Blob([data], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function notesToPdfBlob(notes) {
  // Uses jsPDF if installed. If not installed, throw a friendly error.
  let jsPDF;
  try {
    const mod = await import("jspdf");
    jsPDF = mod.jsPDF;
  } catch {
    throw new Error("PDF export requires jsPDF. Install it: npm i jspdf");
  }
  // ---------- PDF DOCUMENT SETUP ----------
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;

  doc.setFontSize(16);
  doc.text("Kuddlecore Notes Export", margin, 50);

  doc.setFontSize(10);
  doc.text(`Exported: ${new Date().toLocaleString()}`, margin, 70);

  let y = 95;
  doc.setFontSize(11);
  // ---------- PDF NOTE GENERATION ----------

  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];

    const titleLine = `Note ${i + 1}`;
    const tagLine = (n.tags && n.tags.length) ? `Tags: ${n.tags.join(" ")}` : "Tags: (none)";
    const createdLine = `Created: ${n.createdAt ? new Date(n.createdAt).toLocaleString() : "N/A"}`;
    const eventLine = `Event: ${n.scheduledAt ? new Date(n.scheduledAt).toLocaleString() : "N/A"}`;
    const contentLines = doc.splitTextToSize(String(n.content || ""), maxWidth);

    // page break
    if (y > 720) {
      doc.addPage();
      y = 60;
    }

    doc.setFont(undefined, "bold");
    doc.text(titleLine, margin, y);
    y += 16;

    doc.setFont(undefined, "normal");
    doc.text(tagLine, margin, y);
    y += 14;

    doc.text(createdLine, margin, y);
    y += 14;

    doc.text(eventLine, margin, y);
    y += 14;

    for (const line of contentLines) {
      if (y > 740) {
        doc.addPage();
        y = 60;
      }
      doc.text(line, margin, y);
      y += 13;
    }

    y += 12;
  }

  // ---------- PDF OUTPUT ----------

  const arrayBuffer = doc.output("arraybuffer");

  return new Blob([arrayBuffer], {
    type: "application/pdf",
  });
}

// ==================================================
// USER & CENTER HELPERS
//
// Purpose:
// Retrieves information about the current user
// and active center.
//
// Examples:
// Current Center
// Current User Context
//
// ==================================================

function getCenterId() {
  const user =
    JSON.parse(localStorage.getItem("kuddleUser") || "{}") ||
    JSON.parse(sessionStorage.getItem("kuddleUser") || "{}");

  return (
    user.currentCenterId ||
    user.primaryCenterId ||
    user.centerId ||
    ""
  );
}

// ==================================================
// NOTEPAD COMPONENT
//
// Purpose:
// Main Notepad experience used by both the
// full Notepad page and the KuddleBoard widget.
//
// ==================================================

const Notepad = ({ dashboardMode = false }) => {

  // ==================================================
  // STATE MANAGEMENT
  //
  // Purpose:
  // Stores note data, filters, selections,
  // scheduling, and UI state.
  //
  // ==================================================


  const [notes, setNotes] = useState([]); // Mongo notes: {_id, content, tags, ...}
  const [currentNote, setCurrentNote] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [filterTag, setFilterTag] = useState('');
  const [activeTag, setActiveTag] = useState("all");
  const [audience, setAudience] = useState("self");
  const [targetId, setTargetId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [createReminder, setCreateReminder] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [confirmDeleteNotes, setConfirmDeleteNotes] = useState(false);
  const [noteTemplate, setNoteTemplate] = useState("plain");

  // ==================================================
  // BULK SELECTION STATE
  //
  // Purpose:
  // Tracks multiple selected notes for bulk actions.
  //
  // Examples:
  // Delete Selected
  // Export Selected
  // Promote Selected
  //
  // ==================================================

  const [selectedIds, setSelectedIds] = useState([]);

  // ==================================================
  // MEMOIZED NOTE FILTERS
  //
  // Purpose:
  // Calculates filtered and visible note lists
  // without unnecessary re-renders.
  //
  // Examples:
  // Tag Filters
  // Dashboard View
  // Selected Notes
  //
  // ==================================================

  const filteredNotes = useMemo(() => {
    return notes.filter((note) => {
      const noteTags = note.tags || [];

      const matchesDropdown =
        !filterTag || noteTags.includes(filterTag);

      const matchesClickedTag =
        activeTag === "all" || noteTags.includes(`#${activeTag}`);

      return matchesDropdown && matchesClickedTag;
    });
  }, [notes, filterTag, activeTag]);

  const visibleNotes = dashboardMode ? filteredNotes.slice(0, 3) : filteredNotes;

  const selectedNotes = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return notes.filter(n => selectedSet.has(n._id));
  }, [notes, selectedIds]);

  const isAllVisibleSelected = useMemo(() => {
    if (!filteredNotes.length) return false;
    const selectedSet = new Set(selectedIds);
    return filteredNotes.every(n => selectedSet.has(n._id));
  }, [filteredNotes, selectedIds]);

  // ==================================================
  // NOTE SELECTION ACTIONS
  //
  // Purpose:
  // Handles selecting and unselecting notes.
  //
  // Examples:
  // Select Note
  // Select All
  // Clear Selection
  //
  // ==================================================

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    ));
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredNotes.map(n => n._id);
    if (!visibleIds.length) return;

    if (isAllVisibleSelected) {
      // unselect visible
      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // add visible
      setSelectedIds(prev => {
        const set = new Set(prev);
        visibleIds.forEach(id => set.add(id));
        return Array.from(set);
      });
    }
  };

  const clearSelection = () => setSelectedIds([]);

  // ==================================================
  // NOTE EDITOR ACTIONS
  //
  // Purpose:
  // Manages note creation, editing,
  // resetting, and saving.
  //
  // Examples:
  // Add Note
  // Edit Note
  // Update Note
  // Reset Editor
  //
  // ==================================================

  const resetEditor = () => {
    setEditingId(null);
    setCurrentNote("");
    setTagsInput("");
    setEventDate("");
    setEventTime("");
    setAddToCalendar(false);
    setCreateReminder(false);
    setSendNotification(false);
    setNoteTemplate("plain");
    setTargetId("");
  };


  const getTargetName = () => {
    if (audience === "allStaff") return "All Staff";
    if (audience === "self") {
      const user =
        JSON.parse(localStorage.getItem("kuddleUser") || "{}") ||
        JSON.parse(sessionStorage.getItem("kuddleUser") || "{}");

      return user.name || user.fullName || user.firstName || "Me";
    }

    if (audience === "staff") {
      const staff = staffList.find((s) => String(s._id) === String(targetId));
      return staff?.name || staff?.fullName || staff?.email || "Specific Staff";
    }

    if (audience === "allClassrooms") return "All Classrooms";

    if (audience === "classroom") {
      const room = classrooms.find((c) => String(c._id) === String(targetId));
      return room?.name || "Specific Classroom";
    }

    return "";
  };

  const handleSave = async () => {
    const content = String(currentNote || "").trim();
    const tags = normalizeTags(tagsInput);
    const centerId = getCenterId();

    if (!content) {
      toast.error("Please write a note first.");
      return;
    }

    try {
      const scheduledIso = eventDate
        ? new Date(`${eventDate}T${eventTime || "09:00"}`).toISOString()
        : new Date().toISOString();

      // ✅ EDIT EXISTING NOTE + SYNC LINKED CALENDAR/REMINDER

      if (editingId) {
        const existingNote = notes.find((n) => n._id === editingId);

        const editScheduledIso =
          eventDate && eventTime
            ? new Date(`${eventDate}T${eventTime}`).toISOString()
            : existingNote?.scheduledAt || scheduledIso;

        let reminderId = existingNote?.fromReminderId || "";
        let calendarId = existingNote?.fromCalendarId || "";

        if (createReminder) {
          if (reminderId) {
            await api.put(`/api/reminders/${reminderId}`, {
              message: content,
              remindAt: editScheduledIso,
            });
          } else {
            const reminderRes = await apiCreateReminder({
              message: content,
              audience: reminderAudienceMap[audience] || "role",
              target: audience === "allStaff" ? "staff" : targetId,
              targetName: getTargetName(),
              fromNoteId: existingNote._id,
              remindAt: editScheduledIso,
              centerId,
            });

            reminderId =
              reminderRes?.reminder?._id ||
              reminderRes?._id ||
              "";
          }

          window.dispatchEvent(new Event("kuddle:reminders-updated"));
        } else {
          reminderId = "";
        }

        if (addToCalendar) {
          if (calendarId) {
            await api.put(`/api/calendar/${calendarId}`, {
              title: content,
              description: content,
              eventDate: editScheduledIso,
              type: createReminder ? "reminder" : "note",
              fromNoteId: existingNote._id,
              fromReminderId: reminderId,
            });
          } else {
            const calendarRes = await api.post("/api/calendar", {
              title: content,
              description: content,
              eventDate: editScheduledIso,
              type: createReminder ? "reminder" : "note",
              fromNoteId: existingNote._id,
              fromReminderId: reminderId,
              centerId,
              createdBy: "admin",
            });

            calendarId = calendarRes.data?._id || "";
          }

          window.dispatchEvent(new Event("kuddle:calendar-updated"));
        } else {
          calendarId = "";
        }

        if (sendNotification && !createReminder) {
          await apiCreateNotification({
            type: createReminder ? "reminder" : "update",
            title: createReminder ? "Updated Reminder Note" : "Updated Note",
            message: content,
            route: createReminder ? "/reminders" : "/notepad",
            centerId,
            unread: true,
            meta: {
              fromNoteId: String(existingNote._id),
              reminderId: reminderId ? String(reminderId) : "",
              calendarId: calendarId ? String(calendarId) : "",
            },
          });

          window.dispatchEvent(new Event("kuddle:notifications-updated"));
        }
        const updated = await apiUpdateNote(editingId, {
          content,
          tags,
          scheduledAt: editScheduledIso,
          fromReminderId: reminderId,
          fromCalendarId: calendarId,
        });

        setNotes((prev) =>
          prev.map((n) => (n._id === updated._id ? updated : n))
        );

        resetEditor();
        setEventDate("");
        setEventTime("");
        setTargetId("");
        setAddToCalendar(false);
        setCreateReminder(false);
        setSendNotification(false);
        return;
      }

      const created = await apiCreateNote({
        content,
        tags,
        title: "",
        pinned: false,
        centerId,
        createdBy: "admin",
        scheduledAt: scheduledIso,
      });

      let reminderId = "";
      let calendarId = "";

      if (createReminder) {
        const reminderRes = await apiCreateReminder({
          message: created.content,
          audience: reminderAudienceMap[audience] || "role",
          target: audience === "allStaff" ? "staff" : targetId,
          targetName: getTargetName(),
          fromNoteId: created._id,
          remindAt: scheduledIso,
          centerId,
        });

        reminderId =
          reminderRes?.reminder?._id ||
          reminderRes?._id ||
          "";

        window.dispatchEvent(new Event("kuddle:reminders-updated"));
      }

      if (addToCalendar) {
        const calendarRes = await api.post("/api/calendar", {
          title: created.content,
          description: created.content,
          eventDate: scheduledIso,
          type: createReminder ? "reminder" : "note",
          fromNoteId: created._id,
          fromReminderId: reminderId,
          centerId,
          createdBy: "admin",
        });

        calendarId = calendarRes.data?._id || "";
        window.dispatchEvent(new Event("kuddle:calendar-updated"));
      }

      if (sendNotification && !createReminder) {
        await apiCreateNotification({
          type: createReminder ? "reminder" : "update",
          title: createReminder ? "New Reminder Note" : "New Note",
          message: created.content,
          route: createReminder ? "/reminders" : "/notepad",
          centerId,
          unread: true,
          meta: {
            fromNoteId: String(created._id),
            reminderId: reminderId ? String(reminderId) : "",
            calendarId: calendarId ? String(calendarId) : "",
          },
        });

        window.dispatchEvent(new Event("kuddle:notifications-updated"));
      }

      await apiUpdateNote(created._id, {
        fromReminderId: reminderId,
        fromCalendarId: calendarId,
        scheduledAt: scheduledIso,
      });

      window.dispatchEvent(new Event("kuddle:reminders-updated"));

      resetEditor();

      await loadNotes();

    } catch (err) {
      console.error(err);
      toast.error(err.message || "Save failed");
    }
  };

  const reminderAudienceMap = {
    self: "individual",
    allStaff: "role",
    staff: "individual",
    allClassrooms: "all",
    classroom: "classroom",
  };
  const handleEdit = (note) => {
    if (!note?._id) return;

    setEditingId(note._id);
    setCurrentNote(note.content || "");
    setTagsInput((note.tags || [])[0]?.replace("#", "") || "");

    const hasReminder = Boolean(note.fromReminderId);
    const hasCalendar = Boolean(note.fromCalendarId);

    setCreateReminder(hasReminder);
    setAddToCalendar(hasCalendar);
    setSendNotification(false);

    if (hasReminder && hasCalendar) {
      setNoteTemplate("morning");
    } else if (hasReminder) {
      setNoteTemplate("reminder");
    } else if (hasCalendar) {
      setNoteTemplate("calendar");
    } else {
      setNoteTemplate("plain");
    }

    if (note.scheduledAt) {
      const d = new Date(note.scheduledAt);

      if (!Number.isNaN(d.getTime())) {
        setEventDate(d.toISOString().slice(0, 10));
        setEventTime(d.toTimeString().slice(0, 5));
      }
    } else {
      setEventDate("");
      setEventTime("");
    }
  };

  // ==================================================
  // NOTE DELETE ACTIONS
  //
  // Purpose:
  // Handles deleting individual notes
  // and bulk note deletion.
  //
  // Examples:
  // Delete Note
  // Delete Selected
  //
  // ==================================================

  const confirmBulkDeleteNotes = async () => {
    try {
      await apiBulkDelete(selectedIds);
      setNotes((prev) => prev.filter((n) => !selectedIds.includes(n._id)));

      if (editingId && selectedIds.includes(editingId)) resetEditor();

      clearSelection();
      setConfirmDeleteNotes(false);

      window.dispatchEvent(new Event("kuddle:notes-updated"));
      window.dispatchEvent(new Event("kuddle:reminders-updated"));
      window.dispatchEvent(new Event("kuddle:calendar-updated"));
      window.dispatchEvent(new Event("kuddle:notifications-updated"));
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Bulk delete failed");
    }
  };
  const handleDeleteOne = async (note) => {
    if (!note?._id) return;

    try {
      await apiDeleteNote(note._id);

      setNotes((prev) => prev.filter((n) => n._id !== note._id));
      setSelectedIds((prev) => prev.filter((id) => id !== note._id));

      if (editingId === note._id) resetEditor();

      window.dispatchEvent(new Event("kuddle:notes-updated"));
      window.dispatchEvent(new Event("kuddle:reminders-updated"));
      window.dispatchEvent(new Event("kuddle:calendar-updated"));
      window.dispatchEvent(new Event("kuddle:notifications-updated"));


    } catch (err) {
      console.error(err);
      toast.error(err.message || "Delete failed");
    }
  };
  const handleBulkDeleteSelected = () => {
    if (!selectedIds.length) {
      toast.info("No notes selected.");
      return;
    }

    setConfirmDeleteNotes(true);
  };

  // ==================================================
  // NOTE PROMOTION ACTIONS
  //
  // Purpose:
  // Converts notes into reminders and future
  // dashboard-level actions.
  //
  // Examples:
  // Promote to Reminder
  // Pin to Dashboard (future)
  // Feature Note (future)
  // Send to KuddleBoard (future)
  //
  // ==================================================

  const handlePromoteSelectedToReminder = async () => {
    if (!selectedNotes.length) {
      toast.info("Select at least one note to promote.");
      return;
    }

    try {
      for (const n of selectedNotes) {
        await apiCreateReminder({
          message: n.content,
          audience: "role",
          target: "staff",
          targetName: "All Staff",
          fromNoteId: n._id,
          remindAt: new Date().toISOString(),
        });
      }


      clearSelection();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Promote to reminder failed");
    }
  };

  const handlePromoteSingleNote = async (note) => {
    if (!note?._id) return;

    try {
      await apiCreateReminder({
        message: note.content,
        audience: "role",
        target: "staff",
        targetName: "All Staff",
        fromNoteId: note._id,
        remindAt: new Date().toISOString(),
      });

      window.dispatchEvent(new Event("kuddle:reminders-updated"));


    } catch (err) {
      console.error(err);
      toast.error(err.message || "Promote to reminder failed");
    }
  };

  // ==================================================
  // NOTE EXPORT ACTIONS
  //
  // Purpose:
  // Exports note data to downloadable files.
  //
  // Examples:
  // CSV Export
  // PDF Export
  //
  // ==================================================
  const handleExportSelectedCsv = async () => {
    const exportNotes = selectedNotes.length ? selectedNotes : filteredNotes;

    if (!exportNotes.length) {
      toast.info("No notes to export.");
      return;
    }

    try {
      const csv = notesToCsv(exportNotes);
      const filename = `kuddlecore-notes-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadBlob(filename, "text/csv;charset=utf-8", csv);

      await apiLogExport({
        entity: "notes",
        format: "csv",
        count: exportNotes.length,
        ids: exportNotes.map(n => n._id),
        createdBy: "admin",
        centerId: getCenterId(),
        at: new Date().toISOString(),
      });


    } catch (err) {
      console.error(err);
      toast.error(err.message || "CSV export failed");
    }
  };

  const handleExportSelectedPdf = async () => {
    const exportNotes = selectedNotes.length ? selectedNotes : filteredNotes;

    if (!exportNotes.length) {
      toast.info("No notes to export.");
      return;
    }

    try {
      const pdfBlob = await notesToPdfBlob(exportNotes);
      const filename = `kuddlecore-notes-${new Date().toISOString().slice(0, 10)}.pdf`;
      downloadBlob(filename, "application/pdf", pdfBlob);

      await apiLogExport({
        entity: "notes",
        format: "pdf",
        count: exportNotes.length,
        ids: exportNotes.map(n => n._id),
        createdBy: "admin",
        centerId: getCenterId(),
        at: new Date().toISOString(),
      });


    } catch (err) {
      console.error(err);
      toast.error(err.message || "PDF export failed");
    }
  };

  // ==================================================
  // DATA LOADING
  //
  // Purpose:
  // Loads notes, staff, classrooms,
  // and refresh listeners.
  //
  // ==================================================

  useEffect(() => {
    const loadTargets = async () => {
      try {
        const [staffRes, classRes] = await Promise.all([
          api.get("/api/users", { params: { role: "staff" } }),
          api.get("/api/classrooms", { params: { centerId: getCenterId() } }),
        ]);

        setStaffList(Array.isArray(staffRes.data) ? staffRes.data : []);
        setClassrooms(Array.isArray(classRes.data) ? classRes.data : []);
      } catch (err) {
        console.error("Failed to load targets:", err);
      }
    };

    loadTargets();
  }, []);

  const loadNotes = async () => {
    try {
      const dbNotes = await apiGetNotes({ centerId: getCenterId() });
      setNotes(Array.isArray(dbNotes) ? dbNotes : []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load notes");
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  useEffect(() => {
    const refreshNotes = () => {
      loadNotes();
    };

    window.addEventListener("kuddle:notes-updated", refreshNotes);

    return () => {
      window.removeEventListener("kuddle:notes-updated", refreshNotes);
    };
  }, []);

  // ==================================================
  // RENDER UI
  //
  // Purpose:
  // Displays the Notepad user interface.
  //
  // ==================================================

  return (
    <div className={`notepad-container ${dashboardMode ? "dashboard" : ""}`}>
      {/* =========================================
        NOTE TEMPLATE SELECTION
        ========================================= */}
      <div className="template-row">
        <KuddleSelect
          label="Create As"
          value={noteTemplate}
          onChange={(value) => {
            setNoteTemplate(value);

            if (value === "plain") {
              setAddToCalendar(false);
              setCreateReminder(false);
              setSendNotification(false);
            }

            if (value === "reminder") {
              setAddToCalendar(false);
              setCreateReminder(true);
              setSendNotification(false);
            }

            if (value === "calendar") {
              setAddToCalendar(true);
              setCreateReminder(false);
              setSendNotification(false);
            }

            if (value === "announcement") {
              setAddToCalendar(false);
              setCreateReminder(false);
              setSendNotification(true);
            }

            if (value === "morning") {
              setAddToCalendar(true);
              setCreateReminder(true);
              setSendNotification(true);
              setAudience("allStaff");
            }
          }}
          options={[
            { value: "plain", label: "Plain Note" },
            { value: "reminder", label: "Reminder" },
            { value: "calendar", label: "Calendar Event" },
            { value: "announcement", label: "Staff Announcement" },
            { value: "morning", label: "Morning Message" },
          ]}
        />
      </div>

      {/* =========================================
    NOTE EDITOR
    ========================================= */}
      <textarea
        rows={1}
        placeholder={dashboardMode ? "Add a quick note..." : "Write your note here..."}
        value={currentNote}
        onChange={(e) => {
          setCurrentNote(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        }}
        className="note-input"
      />
      {/* =========================================
    TAGS, AUDIENCE & SCHEDULING
    ========================================= */}
      <div className={`notepad-tag-area ${dashboardMode ? "compact" : ""}`}>
        <KuddleSelect
          value={tagsInput}
          onChange={setTagsInput}
          options={[
            { value: "", label: "Select tag" },
            { value: "urgent", label: "Urgent" },
            { value: "staff", label: "Staff" },
            { value: "classroom", label: "Classroom" },
            { value: "finance", label: "Finance" },
            { value: "compliance", label: "Compliance" }
          ]}
        />
        <div className="note-audience-row">
          <label>Send to:</label>

          <KuddleSelect
            value={audience}
            onChange={(value) => {
              setAudience(value);
              setTargetId("");
            }}
            menuPlacement="top"
            options={[
              { value: "self", label: "Myself" },
              { value: "allStaff", label: "All Staff" },
              { value: "staff", label: "Specific Staff" },
              { value: "allClassrooms", label: "All Classrooms" },
              { value: "classroom", label: "Specific Classroom" },
            ]}
          />

          {(audience === "staff" || audience === "classroom") && (
            <KuddleSelect
              value={targetId}
              onChange={setTargetId}
              menuPlacement="top"
              options={[
                { value: "", label: "Select target" },

                ...(audience === "staff"
                  ? staffList.map((staff) => ({
                    value: staff._id,
                    label: staff.name,
                  }))
                  : []),

                ...(audience === "classroom"
                  ? classrooms.map((room) => ({
                    value: room._id,
                    label: room.name,
                  }))
                  : []),
              ]}
            />
          )}
        </div>

        <label style={{ marginTop: 10 }}>
          Reminder Date
        </label>


        <div className="note-date-time-row">
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
          />

          <KuddleSelect
            value={eventTime}
            onChange={setEventTime}
            menuPlacement="top"
            options={[
              { value: "", label: "Select time" },
              { value: "07:00", label: "7:00 AM" },
              { value: "07:30", label: "7:30 AM" },
              { value: "08:00", label: "8:00 AM" },
              { value: "08:30", label: "8:30 AM" },
              { value: "09:00", label: "9:00 AM" },
              { value: "09:30", label: "9:30 AM" },
              { value: "10:00", label: "10:00 AM" },
              { value: "10:30", label: "10:30 AM" },
              { value: "11:00", label: "11:00 AM" },
              { value: "11:30", label: "11:30 AM" },
              { value: "12:00", label: "12:00 PM" },
              { value: "12:30", label: "12:30 PM" },
              { value: "13:00", label: "1:00 PM" },
              { value: "13:30", label: "1:30 PM" },
              { value: "14:00", label: "2:00 PM" },
              { value: "14:30", label: "2:30 PM" },
              { value: "15:00", label: "3:00 PM" },
              { value: "15:30", label: "3:30 PM" },
              { value: "16:00", label: "4:00 PM" },
              { value: "16:30", label: "4:30 PM" },
              { value: "17:00", label: "5:00 PM" },
              { value: "17:30", label: "5:30 PM" },
              { value: "18:00", label: "6:00 PM" }
            ]}
          />
        </div>

        <div className="note-destination-row">
          <label>
            <input
              type="checkbox"
              checked={addToCalendar}
              onChange={(e) => setAddToCalendar(e.target.checked)}
            />
            Add to Calendar
          </label>

          <label>
            <input
              type="checkbox"
              checked={createReminder}
              onChange={(e) => setCreateReminder(e.target.checked)}
            />
            Create Reminder
          </label>

          <label>
            <input
              type="checkbox"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
            />
            Send Notification
          </label>
        </div>


        <p className="note-auto-sync-text">
          Created time is saved automatically. Choose when this reminder or event should occur.
        </p>

      </div>
      {/* =========================================
    SAVE ACTIONS
    ========================================= */}
      <div
        className="notepad-actions-row"
        style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
      >
        <button className="save-btn" onClick={handleSave}>
          {editingId ? "Update Note" : "Add Note"}
        </button>

        {editingId && !dashboardMode && (
          <button
            className="save-btn"
            onClick={resetEditor}
            type="button"
            style={{ opacity: 0.9 }}
            title="Cancel editing"
          >
            Cancel
          </button>
        )}
      </div>

      {dashboardMode && <div className="notepad-divider" />}

      {/* =========================================
    BULK ACTIONS
    ========================================= */}
      {
        !dashboardMode && (

          <div className="notepad-actions">
            <div className="notepad-selection-status">
              Selected: {selectedIds.length}
            </div>
            <div className="notepad-actions-row">
              <button onClick={toggleSelectAllVisible}>
                <CheckSquare size={14} /> {isAllVisibleSelected ? "Unselect" : "Select"}
              </button>

              <button onClick={clearSelection}>
                <X size={14} /> Clear Selection
              </button>

              <button onClick={handleBulkDeleteSelected}>
                <Trash2 size={14} /> Delete Selected
              </button>

              <button onClick={handlePromoteSelectedToReminder}>
                <ArrowUpCircle size={14} /> Promote
              </button>

              <button onClick={handleExportSelectedCsv}>
                <FileText size={14} /> CSV
              </button>

              <button onClick={handleExportSelectedPdf}>
                <FileDown size={14} /> PDF
              </button>
            </div>
          </div>
        )
      }


      {/* =========================================
    TAG FILTERS
    ========================================= */}
      {
        !dashboardMode && notes.length > 0 && (

          <div className="tag-filter-container">
            <label htmlFor="tag-filter">Filter by tag: </label>
            <KuddleSelect
              value={filterTag}
              onChange={setFilterTag}
              options={[
                { value: "", label: "All" },
                ...[...new Set(notes.flatMap((note) => note.tags || []))].map((tag) => ({
                  value: tag,
                  label: tag,
                })),
              ]}
            />
          </div>
        )
      }

      {/* =========================================
    NOTES LIST
    ========================================= */}

      {
        !dashboardMode && (

          <ul
            className="note-list"
            aria-live="polite"
            aria-label="Your saved notes"
          >
            {
              visibleNotes.map((note) => (
                <li key={note._id}>
                  <div className="note-row" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    {!dashboardMode && (
                      <input
                        type="checkbox"
                        aria-label="Select note"
                        checked={selectedIds.includes(note._id)}
                        onChange={() => toggleSelectOne(note._id)}
                        style={{ marginTop: 6 }}
                      />
                    )}

                    <div style={{ flex: 1 }}>
                      <div className="note-content">
                        {note.content}
                      </div>

                      <div className="note-date">
                        Created:{" "}
                        {note.createdAt
                          ? new Date(note.createdAt).toLocaleString()
                          : ""}
                      </div>

                      {note.scheduledAt && (
                        <div className="note-date">
                          Event: {new Date(note.scheduledAt).toLocaleString()}
                        </div>
                      )}

                      {note.tags?.length > 0 && (
                        <div className="note-tags">
                          {note.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="tag-badge"
                              onClick={() => setActiveTag(tag.replace("#", ""))}
                            >
                              {tag.replace("#", "")}
                            </span>
                          ))}

                        </div>
                      )}

                      <div className="note-actions">
                        <button onClick={() => handleEdit(note)} title="Edit">✏️</button>
                        <button onClick={() => handleDeleteOne(note)} title="Delete">🗑️</button>

                        {dashboardMode && (
                          <button
                            type="button"
                            className="promote-note-btn"
                            onClick={() => handlePromoteSingleNote(note)}
                            title="Promote to Reminder"
                          >
                            Create Reminder
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))
            }
          </ul >
        )
      }

      {/* =========================================
    EMPTY STATE
    ========================================= */}

      {
        !dashboardMode && !visibleNotes.length && (

          <p className="notepad-empty-message" >
            No notes yet.Add your first note above.
          </p >
        )
      }

      {/* =========================================
    DELETE CONFIRMATION MODAL
    ========================================= */}
      {
        confirmDeleteNotes && (
          <div className="modal-backdrop">
            <div className="modal confirm-modal">
              <h2>Delete selected notes?</h2>

              <p>
                This will delete {selectedIds.length} selected note(s) and move linked
                reminders, calendar items, and notifications to trash.
              </p>

              <div className="modal-footer">
                <button
                  type="button"
                  className="rbtn rbtn-ghost"
                  onClick={() => setConfirmDeleteNotes(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="rbtn rbtn-danger"
                  onClick={confirmBulkDeleteNotes}
                >
                  Delete Notes
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );

};

export default Notepad;